import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  TextField,
  Grid,
  Chip,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import React, { useState } from "react";
import SearchableAutocomplete from "../../../components/SearchableAutocomplete";
import { Vehicle } from "../../../services/api";
import {
  commonDateFieldProps,
  commonTextFieldProps,
} from "../../../utils/styles";
import { smallButtonProp } from "../../../utils/styles";
import { inventoryApi } from "./apiService";
import { useToast } from "../../../components/Toast";

interface AllocateItemModalProps {
  open: boolean;
  onClose: () => void;
  item: any;
  reallocate: boolean;
  loading?: boolean;
  fetchInventoryItems: () => void;
  category: string;
}

interface AllocationFormData {
  vehicle: Vehicle | null;
  startDate: string;
  notes?: string;
  odometerReading?: number;
}

interface InventoryItem {
  id: string;
  name?: string;
  itemName?: string;
  partNumber?: string;
  quantity?: number;
  manufacturer?: string;
  amount?: number;
}

const validationSchema = Yup.object({
  vehicle: Yup.object().nullable().required("Vehicle is required"),
  startDate: Yup.date().required("Assignment date is required"),
  notes: Yup.string().optional(),
  odometerReading: Yup.number().optional(),
});

const AllocateItemModal = ({
  open,
  onClose,
  item,
  reallocate = false,
  fetchInventoryItems,
  category,
}: AllocateItemModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const initialValues: AllocationFormData = {
    vehicle: reallocate ? item?.masterAllocation?.vehicle : null,
    startDate: new Date().toISOString().split("T")[0], // Today's date in YYYY-MM-DD format
    notes: "",
    odometerReading: item?.masterAllocation?.odometerReading || 0,
  };

  const handleSubmit = async (values: AllocationFormData) => {
    if (!item) return;

    try {
      setIsSubmitting(true);

      let oldAllocation = null;
      if (reallocate) {
        oldAllocation = {
          id: item?.masterAllocation?.id,
          vehicle: values.vehicle,
          startDate: values.startDate,
          notes: values.notes,
          odometerReading: values.odometerReading,
        };
      }

      const data = {
        category1Type: "VEHICLE",
        category1Id: values.vehicle?.id,
        category2Type: "INVENTORY_ITEM",
        category2Id: item?.id,
        startDate: values.startDate,
        notes: values.notes,
        odometerReading: values.odometerReading,
      };

      if (reallocate) {
        await inventoryApi.reallocateItem({
          oldAllocation,
          newAllocation: data,
        });
      } else {
        await inventoryApi.allocateItem(data);
      }
      onClose();
      fetchInventoryItems();
      showToast(
        `Item ${reallocate ? "reallocated" : "allocated"} successfully!`,
        "success"
      );
    } catch (error) {
      showToast(
        `Failed to ${
          reallocate ? "reallocate" : "allocate"
        } item. Please try again.`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getVehicleOptionLabel = (option: Vehicle | string) => {
    if (typeof option === "string") return option;
    return `${option.vehicleNumber}`;
  };

  const renderVehicleOption = (vehicle: Vehicle) => (
    <Box>
      <Typography variant="body2" fontWeight="medium">
        {vehicle.vehicleNumber}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {vehicle.vehicleType} • {vehicle.fuelType}
      </Typography>
    </Box>
  );

  const getItemDisplayName = (item: InventoryItem) => {
    return item.name || item.itemName || item.partNumber || "Unknown Item";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "0.75rem" }}>
          Allocate Item
          {item && (
            <Chip
              label={`${getItemDisplayName(item)}`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontSize: "0.65rem", ml: 1, borderRadius: "4px" }}
            />
          )}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched, isValid, dirty }) => (
            <Form>
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid container item spacing={1} xs={12} sm={12}>
                  {/* Vehicle Selection */}
                  <Grid item xs={12} sm={6}>
                    <SearchableAutocomplete<Vehicle>
                      entityType="VEHICLE"
                      value={values.vehicle}
                      onSelect={(vehicle) => setFieldValue("vehicle", vehicle)}
                      onChange={() => {}} // Handle text input if needed
                      label="Search Vehicle"
                      placeholder="Enter vehicle number or model..."
                      getOptionLabel={getVehicleOptionLabel}
                      renderOptionContent={renderVehicleOption}
                      noOptionsText="No vehicles found"
                      size="small"
                    />
                    {errors.vehicle && touched.vehicle && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.vehicle as string}
                      </Typography>
                    )}
                  </Grid>

                  {/* Assignment Date */}
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label="Assignment Date"
                      name="startDate"
                      type="date"
                      fullWidth
                      {...commonDateFieldProps}
                      error={touched.startDate && Boolean(errors.startDate)}
                      helperText={touched.startDate && errors.startDate}
                    />
                  </Grid>
                </Grid>

                {category === "Tyre" && (
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label="Km's Reading"
                      name="odometerReading"
                      value={values.odometerReading?.toString() || ""}
                      onChange={(e: any) => {
                        setFieldValue(
                          "odometerReading",
                          Number(e.target.value)
                        );
                      }}
                      fullWidth
                      {...commonTextFieldProps}
                      placeholder="Add Km's Reading..."
                      error={
                        touched.odometerReading &&
                        Boolean(errors.odometerReading)
                      }
                      helperText={
                        touched.odometerReading && errors.odometerReading
                      }
                    />
                  </Grid>
                )}
                {/* Notes */}
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    label="Notes"
                    name="notes"
                    multiline
                    rows={1}
                    fullWidth
                    {...commonTextFieldProps}
                    placeholder="Add any additional notes..."
                    error={touched.notes && Boolean(errors.notes)}
                    helperText={touched.notes && errors.notes}
                  />
                </Grid>
              </Grid>

              <DialogActions sx={{ p: 0, pt: 1 }}>
                <Button
                  onClick={onClose}
                  color="inherit"
                  disabled={isSubmitting}
                  {...smallButtonProp}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!dirty || isSubmitting}
                  {...smallButtonProp}
                >
                  {isSubmitting ? "Allocating..." : "Allocate Item"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AllocateItemModal;
